import React from 'react';
import { Link } from 'react-router-dom';
import type { Game } from '@sulis-liiga/common';
import { GameStatus } from '@sulis-liiga/common';
import { teamLabelsOf, gamesWon } from '../../utils/gameTeams';

interface GameCardProps {
  game: Game;
}

const statusColors: Record<string, string> = {
  [GameStatus.SCHEDULED]: 'bg-blue-100 text-blue-800',
  [GameStatus.COMPLETED]: 'bg-green-100 text-green-800',
  [GameStatus.CANCELLED]: 'bg-gray-100 text-gray-800',
  [GameStatus.DISPUTED]: 'bg-yellow-100 text-yellow-800',
};

function statusLabel(game: Game): { label: string; className: string } {
  if (game.status === GameStatus.CANCELLED && game.cancellationReason === 'abandoned') {
    return { label: 'Abandoned', className: 'bg-amber-100 text-amber-800' };
  }
  return {
    label: game.status,
    className: statusColors[game.status] || 'bg-gray-100 text-gray-800',
  };
}

const GameCard: React.FC<GameCardProps> = ({ game }) => {
  const formattedDate = game.scheduledDate
    ? new Date(game.scheduledDate).toLocaleDateString()
    : 'Not scheduled';
  const [teamAId, teamBId] = game.teamIds;
  const [teamALabel, teamBLabel] = teamLabelsOf(game);

  return (
    <Link to={`/games/${game.id}`} className="block">
      <div className="bg-white shadow rounded-lg p-6 hover:shadow-md transition-shadow">
        <div className="flex justify-between items-start mb-4">
          {(() => {
            const { label, className } = statusLabel(game);
            return (
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${className}`}
              >
                {label}
              </span>
            );
          })()}
          <span className="text-sm text-gray-500">{formattedDate}</span>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between items-center">
            <span className="text-sm font-medium text-gray-900">{teamALabel}</span>
            <span className="text-sm text-gray-400">vs</span>
            <span className="text-sm font-medium text-gray-900">{teamBLabel}</span>
          </div>
        </div>

        {game.result && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="text-center">
              <span className="text-lg font-bold text-gray-900">
                {gamesWon(game.result, teamAId)} - {gamesWon(game.result, teamBId)}
              </span>
              {game.result.verified && (
                <span className="ml-2 text-xs text-green-600">Verified</span>
              )}
            </div>
          </div>
        )}

        <div className="mt-4 text-sm text-gray-500">
          <p>
            {game.scheduledTime} at {game.venue?.name}
          </p>
        </div>
      </div>
    </Link>
  );
};

export default GameCard;
